/* eslint-disable react/jsx-fragments */
import React from "react";

import Nav from "reactstrap/lib/Nav";
import NavItem from "reactstrap/lib/NavItem";
import NavLink from "reactstrap/lib/NavLink";
import UncontrolledDropdown from "reactstrap/lib/UncontrolledDropdown";
import DropdownToggle from "reactstrap/lib/DropdownToggle";
import DropdownMenu from "reactstrap/lib/DropdownMenu";
import Button from "reactstrap/lib/Button";
import ChevronDefaultRight from "@druva-apps/orion-icons/lib/ChevronsmallrightGreyOutline";
import UserProfileSolid from "@druva-apps/orion-icons/lib/UserprofileGreyFilled";

import { gatherConditionalProps, isFunction, gatherClasses } from "./utils";
import DropdownUtils, { dropdownHasSubmenus } from "./dropdownUtils";
import Tooltip from "../components/tooltip/tooltip";
import Icon from "../components/icon/icon";
import OrionButton from "../components/button/button";
import Popover from "../components/popover/popover";
import Progress from "../components/progress/progress";
import DropdownSelect from "../components/headerbar/dropdownSelect";
import OrganizationSelect from "../components/headerbar/organizationSelect";

export const getBadgeColor = isDell => ({
  success: isDell ? "#28A745" : "#208337",
  info: isDell ? "#1B5D95" : "#2E77B2",
  danger: "#DC3545"
});

const renderButton = (button, index) => {
  const { label, icon, disabled, isActive, onClick, className } = button;
  return (
    <NavItem key={index}>
      <OrionButton
        label={label}
        icon={icon}
        disabled={disabled}
        active={isActive}
        onClick={onClick}
        type="primary"
        className={gatherClasses(null, "oui-headerbar-button", className || "")}
      />
    </NavItem>
  );
};

const renderDivider = (data = {}, index) => {
  return (
    <NavItem
      key={index}
      className={gatherClasses(
        null,
        "oui-headerbar-separator",
        data.isLarge === true ? "oui-separator-lg" : "oui-separator-sm",
        data.isInclined === true && "oui-separator-inclined"
      )}
    />
  );
};

const renderLink = (linkObj, index) => {
  return (
    <NavItem key={index}>
      {isFunction(linkObj.renderComponent) ? (
        linkObj.renderComponent(
          linkObj,
          index,
          "oui-active",
          gatherClasses(
            null,
            "oui-nav-link",
            linkObj.isBrandLink && "oui-brand-link"
          )
        )
      ) : (
        <NavLink
          className={gatherClasses(
            null,
            linkObj.isActive && "oui-active",
            linkObj.isBrandLink && "oui-brand-link"
          )}
          {...gatherConditionalProps(
            linkObj,
            "href",
            "target",
            "disabled",
            "onClick"
          )}
        >
          {linkObj.name}
        </NavLink>
      )}
    </NavItem>
  );
};

const renderDropdown = (dropdownObj, index) => {
  let menuStyle = {};
  const { items } = dropdownObj;
  const hasSubmenus = dropdownHasSubmenus(items);

  if (dropdownObj.maxMenuHeight) {
    const maxHeight = String(dropdownObj.maxMenuHeight).endsWith("px")
      ? dropdownObj.maxMenuHeight
      : `${dropdownObj.maxMenuHeight}px`;
    menuStyle = { maxHeight, overflowY: "auto" };
  }

  if (hasSubmenus) {
    menuStyle = { maxHeight: "none", overflow: "visible" };
  }

  return (
    <UncontrolledDropdown
      nav
      inNavbar
      key={index}
      active={dropdownObj.isActive}
    >
      <DropdownToggle
        nav
        disabled={dropdownObj.disabled || dropdownObj.disabledTrigger}
      >
        {dropdownObj.label}&nbsp;
        <ChevronDefaultRight
          inheritFontColor
          size="xs"
          width={11}
          height={11}
          muted={false}
          className="oui-dropdown-caret"
        />
      </DropdownToggle>
      <DropdownMenu right={dropdownObj.rightAligned} style={menuStyle}>
        {new DropdownUtils(hasSubmenus).renderDropdownItems(dropdownObj)}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const renderSelect = (selectObj, index) => {
  return <DropdownSelect key={index} {...selectObj} />;
};

const renderIconTooltip = (tooltip, icon) => {
  if (tooltip && tooltip.text) {
    return (
      <Tooltip
        text={tooltip.text}
        placement={tooltip.placement || "top"}
        className={tooltip.className || null}
      >
        {icon}
      </Tooltip>
    );
  }
  return icon;
};

const renderIcon = (iconObj, index, context) => {
  return (
    <li className="oui-nav-item" key={index}>
      <a
        href={iconObj.href || "#"}
        target={iconObj.target}
        disabled={iconObj.disabled}
        className={gatherClasses(
          null,
          "oui-nav-link",
          "oui-icon-badge-container",
          iconObj.isActive ? "oui-active" : null
        )}
        aria-label={iconObj.title}
        onClick={!iconObj.disabled ? iconObj.onClick : null}
      >
        {renderIconTooltip(
          iconObj.tooltip,
          <React.Fragment>
            {iconObj.icon ? (
              React.isValidElement(iconObj.icon) ? (
                <div className="oui-headerbar-icon oui-headerbar-icon-new">
                  {React.cloneElement(iconObj.icon, {
                    className: gatherClasses(
                      null,
                      "oui-icon",
                      iconObj.icon.props.className
                    ),
                    muted: false,
                    inheritFontColor: true
                  })}
                </div>
              ) : (
                <div className="oui-headerbar-icon oui-headerbar-icon-old">
                  <Icon {...iconObj.icon} muted={false} className="oui-icon" />
                </div>
              )
            ) : null}

            {iconObj.badge ? (
              <button
                type="button"
                className={gatherClasses(
                  null,
                  "oui-badge",
                  iconObj.badge.onClick ? "oui-clickable" : null
                )}
                style={{
                  backgroundColor:
                    getBadgeColor(context && context.isDellApplication())[
                      iconObj.badge.type
                    ] || iconObj.badge.color
                }}
                onClick={
                  isFunction(iconObj.badge.onClick)
                    ? e => {
                        e.stopPropagation();
                        iconObj.badge.onClick(e);
                      }
                    : null
                }
              >
                {iconObj.badge.label}
              </button>
            ) : null}
          </React.Fragment>
        )}
      </a>
    </li>
  );
};

const renderIconDropdown = (idObj, index, context) => {
  let menuStyle = {};
  const { maxMenuHeight, items } = idObj.dropdown || {};
  const hasSubmenus = dropdownHasSubmenus(items);

  if (maxMenuHeight) {
    const maxHeight = String(maxMenuHeight).endsWith("px")
      ? maxMenuHeight
      : `${maxMenuHeight}px`;
    menuStyle = { maxHeight, overflowY: "auto" };
  }

  if (hasSubmenus) {
    menuStyle = { maxHeight: "none", overflow: "visible" };
  }

  return (
    <UncontrolledDropdown nav inNavbar key={index}>
      <DropdownToggle
        nav
        className={gatherClasses(
          null,
          "oui-icon-badge-container",
          idObj.isActive ? "active" : null
        )}
        aria-label={idObj.title}
      >
        {renderIconTooltip(
          idObj.tooltip,
          <React.Fragment>
            {idObj.icon ? (
              React.isValidElement(idObj.icon) ? (
                <div className="oui-headerbar-icon oui-headerbar-icon-new">
                  {React.cloneElement(idObj.icon, {
                    className: gatherClasses(
                      null,
                      "oui-icon",
                      idObj.icon.props.className
                    ),
                    muted: false,
                    inheritFontColor: true
                  })}
                </div>
              ) : (
                <div className="oui-headerbar-icon oui-headerbar-icon-old">
                  <Icon {...idObj.icon} muted={false} className="oui-icon" />
                </div>
              )
            ) : (
              <span className="oui-initials">{idObj.initials}</span>
            )}
            {idObj.badge ? (
              <span
                className="oui-badge"
                style={{
                  backgroundColor:
                    getBadgeColor(context && context.isDellApplication())[
                      idObj.badge.type
                    ] || idObj.badge.color
                }}
              >
                {idObj.badge.label}
              </span>
            ) : null}
          </React.Fragment>
        )}
      </DropdownToggle>

      <DropdownMenu right={idObj.dropdown.rightAligned} style={menuStyle}>
        {new DropdownUtils(hasSubmenus).renderDropdownItems(idObj.dropdown)}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const renderIconPopover = (idObj, index, context) => {
  // Remove/override onClick if provided as it is not needed in normal headerbar
  const item = { ...idObj, onClick: null };
  return (
    <Popover
      {...idObj.popover}
      disabled={idObj.disabled}
      hideArrow
      key={index}
      className="oui-headerbar-icon-popover"
    >
      {renderIcon(item, index, context)}
    </Popover>
  );
};

const renderOrganizationSelect = (obj, index) => {
  return (
    <li className="oui-headerbar-org-select" key={index}>
      <OrganizationSelect {...obj} />
    </li>
  );
};

const renderProgress = (obj, index) => {
  const { value, valueLabel, label } = obj;
  let sizeClass = "";

  if (valueLabel) {
    const valueLabelLength = String(valueLabel);
    if (valueLabelLength.length === 2) {
      sizeClass = "oui-value-lg";
    } else if (valueLabelLength.length === 3) {
      sizeClass = "oui-value-md";
    } else if (valueLabelLength.length === 4) {
      sizeClass = "oui-value-sm";
    } else if (valueLabelLength.length >= 5) {
      sizeClass = "oui-value-xs";
    }
  }

  return (
    <NavItem key={index}>
      <Progress
        className={gatherClasses(
          null,
          "oui-headerbar-progress",
          sizeClass,
          "oui-pl-2",
          "oui-pr-2"
        )}
        type="circular"
        value={value}
        valueLabel={valueLabel}
        max={100}
        size="sm"
        showPercProgress
        label={label}
        labelPlacement="right"
      />
    </NavItem>
  );
};

const renderProfile = (profile, index) => {
  function closeProfileDropdown() {
    const toggler = document.querySelector("#hb-profile-toggler");
    if (toggler && toggler instanceof HTMLElement) {
      toggler.click();
    }
  }

  const { initials } = profile.user;
  return (
    <UncontrolledDropdown nav inNavbar key={index} active={profile.isActive}>
      <DropdownToggle
        nav
        className="oui-icon-badge-container oui-headerbar-profile-toggler"
        aria-label="Profile Menu"
        id="hb-profile-toggler"
      >
        {renderIconTooltip(
          profile.tooltip,
          <UserProfileSolid width={20} height={20} inheritFontColor />
        )}
      </DropdownToggle>

      <DropdownMenu right className="oui-profile-menus">
        <div className="oui-profile-container">
          <h2 className="oui-profile-avatar">{initials}</h2>
          <div className="oui-user-details">
            <span className="oui-profile-username oui-text-truncate">
              <a
                href={profile.user.href}
                target={profile.user.target}
                onClick={e => {
                  closeProfileDropdown();
                  if (isFunction(profile.user.onClick)) {
                    profile.user.onClick(e);
                  }
                }}
              >
                {profile.user.name}
              </a>
            </span>
            <span className="oui-profile-email oui-text-truncate">
              {profile.user.email}
            </span>
          </div>
        </div>
        {profile.links && profile.links.length > 0 && (
          <div className="oui-extra-links">
            <Nav vertical>
              {profile.links.map((linkObj, indx) => {
                return (
                  <NavItem key={indx}>
                    {isFunction(linkObj.renderComponent) ? (
                      linkObj.renderComponent(
                        linkObj,
                        "oui-nav-link",
                        closeProfileDropdown
                      )
                    ) : (
                      <NavLink
                        active={linkObj.isActive}
                        {...gatherConditionalProps(
                          linkObj,
                          "href",
                          "target",
                          "disabled"
                        )}
                        role="menuitem"
                        tabIndex="0"
                        onClick={e => {
                          closeProfileDropdown();
                          if (isFunction(linkObj.onClick)) {
                            linkObj.onClick(e, linkObj);
                          }
                        }}
                      >
                        {linkObj.name}
                      </NavLink>
                    )}
                  </NavItem>
                );
              })}
            </Nav>
          </div>
        )}
        {isFunction(profile.renderContent) && (
          <div className="oui-profile-content oui-p-3 oui-border-bottom">
            {profile.renderContent(closeProfileDropdown)}
          </div>
        )}
        <div className="oui-logout-menu">
          <Button
            color="primary"
            size="sm"
            outline
            tag="a"
            href={profile.logout.href || "#"}
            target={profile.logout.target}
            onClick={e => {
              closeProfileDropdown();
              if (isFunction(profile.logout.onClick)) {
                profile.logout.onClick(e);
              }
            }}
          >
            <span>{profile.logout.label || "Sign out"}</span>
          </Button>
        </div>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default function renderComponents(components, context) {
  if (!components || components.length === 0) {
    return [];
  }

  return components.map((item, index) => {
    if (item.component === "divider") {
      return renderDivider(item.data, index);
    }
    if (item.component === "link") {
      return renderLink(item.data, index);
    }
    if (item.component === "dropdown") {
      return renderDropdown(item.data, index);
    }
    if (item.component === "select") {
      return renderSelect(item.data, index);
    }
    if (item.component === "icon") {
      return renderIcon(item.data, index, context);
    }
    if (item.component === "icon-dropdown") {
      return renderIconDropdown(item.data, index, context);
    }
    if (item.component === "icon-popover") {
      return renderIconPopover(item.data, index, context);
    }
    if (item.component === "profile") {
      return renderProfile(item.data, index);
    }
    if (item.component === "button") {
      return renderButton(item.data, index);
    }
    if (item.component === "organization-select") {
      return renderOrganizationSelect(item.data, index);
    }
    if (item.component === "progress") {
      return renderProgress(item.data, index);
    }
    throw new Error(
      `Unsupported component '${item.component}' to render on the Headerbar`
    );
  });
}

export function renderLeftComponents(components, context) {
  const renderableComponents = renderComponents(components, context);

  if (renderableComponents.length > 0) {
    return [...renderableComponents];
  }

  return renderableComponents;
}

export function renderRightComponents(components, context) {
  return renderComponents(components, context);
}
